"use client";

import { FlaggedAccountMessage } from "@/components/WeeklyRewards/Redemption/FlaggedAccountMessage";
import { useWeeklyRewards } from "@/components/WeeklyRewards/WeeklyRewardsProvider";
import { ONBOARDING_QUEST_ID } from "@/constants";
import { Action, useOnboardingAnalytics } from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { useAccessToken } from "@/hooks";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { useReferralAcceptance } from "@/hooks/useReferralAcceptance";
import { cn } from "@/lib/utils";
import { notifyError } from "@/utils/monitoring";
import { trackPixelEvent } from "@/utils/pixel";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { LensFlareBackground } from "../components/LensFlareBackground";
import { RewardBackground } from "../components/RewardBackground";
import { OnboardingStep } from "../constants";
import { DoQuestsInfo } from "./DoQuestsInfo";
import { ExploreGamesInfo } from "./ExploreGamesInfo";
import { FreeEntryInfo } from "./FreeEntryInfo";
import { FreeEntryUnlockedInfo } from "./FreeEntryUnlockedInfo";
import { GenreSelector } from "./GenreSelector";
import { SeeProgressInfo } from "./SeeProgressInfo";
import { WelcomeInfo } from "./WelcomeInfo";

const STEPS = [
  WelcomeInfo,
  FreeEntryInfo,
  GenreSelector,
  ExploreGamesInfo,
  DoQuestsInfo,
  SeeProgressInfo,
  FreeEntryUnlockedInfo,
];

const RenderSteps = ({
  currentStep,
  steps,
}: {
  currentStep: number;
  steps: React.ComponentType<{ visible: boolean }>[];
}) => (
  <div className="relative overflow-hidden rounded-xl">
    <div
      className="flex h-full transition-transform duration-500 ease-in-out"
      style={{
        transform: `translateX(-${currentStep * 100}%)`,
      }}
    >
      {steps.map((Step, index) => (
        <div
          key={Step.name}
          className={cn(
            "w-full flex-shrink-0 transition-opacity duration-500",
            index === currentStep ? "opacity-100" : "opacity-0",
          )}
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "calc(100dvh-64px) 100vw",
          }}
        >
          <Step visible={index === currentStep} />
        </div>
      ))}
    </div>
  </div>
);

export const OnboardingFlow = ({
  isModal = false,
}: {
  isModal?: boolean;
}) => {
  const { currentStep, totalSteps, setTotalSteps } = useOnboardingStep();
  const { isAccountFlagged } = useWeeklyRewards();
  const getAccessToken = useAccessToken();
  const hasViewedRef = useRef(false);
  const onboardingCompletedRef = useRef(false);
  const { track } = useOnboardingAnalytics();
  const { handleAcceptReferral } = useReferralAcceptance();
  const referralCode = useSearchParams()?.get("ref")?.trim();
  const router = useRouter();
  const { redeemTicket, isParticipatingInTheCurrentSweepstake } =
    useWeeklyRewards();

  useEffect(() => trackPixelEvent("OnboardingFlow", {}), []);

  useEffect(() => {
    if (hasViewedRef.current) return;
    hasViewedRef.current = true;
    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.ONBOARDING_FLOW,
        isAccountFlagged,
        step: -1,
      },
    });
  }, [track, isAccountFlagged]);

  useEffect(() => {
    setTotalSteps(STEPS.length);
  }, [setTotalSteps]);

  const claimSweepstakeTicket = async () => {
    const token = await getAccessToken();
    await redeemTicket(false, token);
  };

  // Complete onboarding quest: immediately for modal, at last step for non-modal
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const isOnboardingComplete = isModal
      ? true
      : currentStep === totalSteps - 1 && totalSteps > 0;

    if (isOnboardingComplete && !onboardingCompletedRef.current) {
      const completeOnboardingQuest = async () => {
        try {
          const error = await submitQuestCompletion(
            ONBOARDING_QUEST_ID,
            getAccessToken,
          );

          if (!error) {
            onboardingCompletedRef.current = true;
            track({
              action: Action.SUCCEEDED,
              extras: {
                id: "onboarding-quest",
              },
            });

            // Redeem ticket after quest completion succeeds (paid UA only)
            // Modal users will redeem at the last step via the separate effect
            if (!isModal) await claimSweepstakeTicket();
          } else {
            onboardingCompletedRef.current = false;
            notifyError(error, "questCompletionError", {
              questId: ONBOARDING_QUEST_ID,
            });
          }
        } catch (error) {
          onboardingCompletedRef.current = false;
          notifyError(error, "questCompletionError", {
            questId: ONBOARDING_QUEST_ID,
          });
        }
      };
      completeOnboardingQuest();
    }
  }, [currentStep, totalSteps, getAccessToken, track, isModal, redeemTicket]);

  // Redeem ticket at the last step for modal users
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    (async () => {
      if (!isModal) return;

      const isLastStep = currentStep === totalSteps - 1 && totalSteps > 0;
      if (!isLastStep) return;

      if (!onboardingCompletedRef.current) return;

      await claimSweepstakeTicket();
    })();
  }, [isModal, currentStep, totalSteps]);

  // Auto-accept referral in the background if present and redirect to homepage
  useEffect(() => {
    if (referralCode) {
      handleAcceptReferral(referralCode).catch(() => {
        // Silently ignore errors - already handled by the hook
      });

      // Redirect to homepage after accepting referral
      router.replace("/quests");
    }
  }, [referralCode, handleAcceptReferral, router]);

  // Don't render anything if referralCode is present - just redirect
  if (referralCode) {
    return null;
  }

  return (
    <div className="relative w-full h-full overflow-auto flex rounded-xl">
      <RewardBackground />
      <LensFlareBackground />

      {isAccountFlagged ? (
        <FlaggedAccountMessage />
      ) : (
        <RenderSteps currentStep={currentStep} steps={STEPS} />
      )}
    </div>
  );
};
